// Story 16.7 — cómo se lee cada entrada del audit log en el dashboard.

export interface AuditEntry {
  id: number;
  at: string;
  action: string;
  actorUserId: number | null;
  actorEmail: string | null;
  organizationId: number | null;
  organizationName: string | null;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown>;
  /** Solo para el admin. */
  ip?: string | null;
}

export const AUDIT_CATEGORY_LABELS: Record<string, string> = {
  'auth.': 'Accesos',
  'user.': 'Cuentas',
  'organization.': 'Organizaciones',
  'invitation.': 'Invitaciones',
  'member.': 'Miembros',
  'bot.': 'Bots',
  'credentials.': 'Credenciales',
  'session.': 'Sesiones',
};

const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Inició sesión',
  'auth.login_failed': 'Login fallido',
  'auth.api_key_issued': 'Creó una API key',
  'auth.api_key_revoked': 'Revocó una API key',
  'auth.password_changed': 'Cambió su contraseña',
  'auth.password_reset': 'Usó un link de reset',
  'user.password_reset_created': 'Generó un link de reset',
  'user.disabled': 'Desactivó una cuenta',
  'user.enabled': 'Reactivó una cuenta',
  'organization.created': 'Creó la organización',
  'organization.renamed': 'Renombró la organización',
  'invitation.created': 'Invitó',
  'invitation.revoked': 'Revocó una invitación',
  'invitation.accepted': 'Aceptó una invitación',
  'member.role_changed': 'Cambió un rol',
  'member.removed': 'Quitó a un miembro',
  'bot.created': 'Creó un bot',
  'bot.deleted': 'Borró un bot',
  'bot.transferred': 'Movió un bot',
  'bot.deployed': 'Deployó',
  'bot.rolled_back': 'Hizo rollback',
  'credentials.updated': 'Cambió credenciales',
  'credentials.deleted': 'Borró credenciales',
  'session.expired': 'Expiró una sesión',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

const text = (v: unknown): string => (v == null ? '—' : String(v));

/** Una línea con lo importante de la entrada: a quién, qué cambió. */
export function describeEntry(entry: AuditEntry): string {
  const m = entry.metadata ?? {};
  const target = entry.targetId ? `#${entry.targetId}` : '';
  switch (entry.action) {
    case 'auth.login_failed':
      return `email ${text(m.emailPseudonym)}`;
    case 'auth.api_key_issued':
      return `"${text(m.label)}"`;
    case 'organization.created':
      return text(m.name);
    case 'organization.renamed':
      return `${text(m.from)} → ${text(m.to)}`;
    case 'invitation.created':
      return `${text(m.email)} como ${text(m.role)}`;
    case 'invitation.accepted':
      return `como ${text(m.role)}${m.newAccount ? ' (cuenta nueva)' : ''}`;
    case 'member.role_changed':
      return `usuario ${target}: ${text(m.from)} → ${text(m.to)}`;
    case 'member.removed':
      return `usuario ${target} (${text(m.role)})`;
    case 'bot.created':
    case 'bot.deleted':
      return `${text(m.name)} ${target}`;
    case 'bot.transferred':
      return `${text(m.name)}: organización #${text(m.fromOrganizationId)} → #${text(m.toOrganizationId)}`;
    case 'bot.deployed':
      return `bot ${target} v${text(m.version)}`;
    case 'bot.rolled_back':
      return `bot ${target}: v${text(m.fromVersion)} → v${text(m.toVersion)}`;
    case 'credentials.updated':
      return `${text(m.provider)}: ${Array.isArray(m.fields) ? m.fields.join(', ') : '—'}`;
    case 'credentials.deleted':
      return text(m.provider);
    case 'session.expired':
      return `sesión ${target} del bot #${text(m.botId)}`;
    default:
      return entry.targetType ? `${entry.targetType} ${target}` : '';
  }
}
